import domPurify, { type Config } from 'dompurify'

/**
 * Restrictive allow-list for search-index highlight snippets. The backend
 * wraps matched terms in <mark>; only inline formatting tags survive and every
 * attribute is stripped, blocking scripts, event handlers, links and styles.
 */
const SEARCH_HIGHLIGHT_CONFIG: Config = {
  ALLOWED_ATTR: [],
  ALLOWED_TAGS: ['mark', 'em', 'strong', 'b', 'i', 'span'],
}

// DOMPurify needs a DOM. In a non-browser context (SSR/pre-render) the default
// export is an uninitialised factory and calling .sanitize would throw, so we
// guard on `window`. We fail CLOSED (drop the markup) rather than returning the
// raw input — a sanitizer must never let unsanitised HTML through.
const canSanitize = typeof window !== 'undefined'

/**
 * Sanitize backend-controlled HTML before injecting it through
 * `dangerouslySetInnerHTML`. Removes scripts, event handlers and other
 * script-execution vectors (DOMPurify default profile).
 *
 * Note: the default profile intentionally PRESERVES inline `style` attributes
 * and formatting markup so rich CMS content and the equipment-usage-agreement
 * contract template render correctly. CSS is not scrubbed, so this must only be
 * used for trusted (backend/admin-controlled) HTML — never for arbitrary user
 * input. For search highlight snippets use `sanitizeSearchHighlight`, which
 * strips every attribute.
 */
export const sanitizeHtml = (dirty: string | null | undefined): string =>
  canSanitize ? domPurify.sanitize(dirty ?? '') : ''

/**
 * Stricter variant for search highlight snippets — only highlight/formatting
 * tags are kept, everything else (including all attributes) is removed.
 */
export const sanitizeSearchHighlight = (
  dirty: string | null | undefined,
): string =>
  canSanitize ? domPurify.sanitize(dirty ?? '', SEARCH_HIGHLIGHT_CONFIG) : ''
